import React, { useEffect, useState } from 'react';
import { Button, Card, Empty, Space, Table, Typography, Badge } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import usecustomStyles from '../Common/customStyles';
import Spinners from '../Common/Spinner';
import { DeleteModal } from '../Common/DeleteModal';
import { themecolor } from '../config';
import '../assets/scss/_custom.scss';

import * as PThunk from '../slices/project/thunk';
import * as SThunk from '../slices/sites/thunk';
import * as PoleThunk from '../slices/poles/thunk';
import PoleModal from './PoleModal';
import * as HPermission from '../helpers/permission_helper';

const { Link } = Typography;

const customStyles = usecustomStyles();

const TEXT_CONSTANTS = {
    TITLE: '监测杆列表',
    CREATE: '新建'
};

const BaseTableColumns = [
    {
        title: '所属项目',
        dataIndex: 'projectName',
        sorter: (a, b) => a.projectName.localeCompare(b.projectName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '所属工地',
        dataIndex: 'siteName',
        sorter: (a, b) => a.siteName.localeCompare(b.siteName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '位置',
        dataIndex: 'position',
        sorter: (a, b) => a.position.localeCompare(b.position),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
];

function DataList() {
    const selector = createSelector(
        state => state.Poles,
        state => ({
            dataList: state.dataList,
            loading: state.loading,
        }),
    );

    const { dataList, loading } = useSelector(selector);
    const projectList = useSelector(state => state.Projects.dataList);
    const sitesList = useSelector(state => state.Sites.dataList);
    const user = useSelector(state => state.Login.user);

    /**
     * 当前数据列表"加载中"状态
     */
    const [isLoading, setLoading] = useState(loading);

    /**
     * 当前页面的数据列表
     */
    const [localDataList, setLocalDataList] = useState([]);

    /**
     * 当前页面的数据列表的数据列
     */
    const [tableColumns, setTableColumns] = useState([]);

    /**
     * 显示删除"模态框"
     */
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

    /**
     * 显示编辑"模态框"
     */
    const [editModalIsOpen, setEditModalIsOpen] = useState(false);

    /**
     * 当前操作选中的数据行
     */
    const [selectedDataRow, setSelectedDataRow] = useState();

    /**
     * 编辑弹出框是"新建"模式还是"编辑"模式
     */
    const [isCreateModel, setIsCreateModel] = useState(false);

    /**
     * 显示编辑"新建"按钮
     */
    const [showCreateBtn, setShowCreateBtn] = useState(false);

    /**
     * 显示编辑"编辑"按钮
     */
    const [showEditBtn, setShowEditBtn] = useState(false);

    /**
     * 显示编辑"删除"按钮
     */
    const [showDeleteBtn, setShowDeleteBtn] = useState(false);

    const dispatch = useDispatch();

    /**
     * <--------------------- useEffect --------------------->
     */

    useEffect(() => {
        dispatch(PThunk.getAll());
        dispatch(SThunk.getAll());
        dispatch(PoleThunk.getAll());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!Array.isArray(dataList)) {
            setLocalDataList([]);
            return;
        }

        const projectMap = {};
        for (let i = 0; i < projectList.length; i++) {
            const item = projectList[i];
            projectMap[item.id] = {
                projectId: item.id,
                projectName: item.projectName,
                companyName: item.companyName
            };
        }

        const siteMap = {};
        for (let i = 0; i < sitesList.length; i++) {
            const item = sitesList[i];
            siteMap[item.id] = {
                siteId: item.id,
                siteName: item.siteName,
                projectId: item.projectId,
                projectName: item.projectName || projectMap[item.projectId] || '-',
                companyName: projectMap[item.projectId] || '-'
            };
        }

        const viewDataList = [];
        for (let i = 0; i < dataList.length; i++) {
            const item = { ...dataList[i] };
            viewDataList.push({ ...item, ...(siteMap[item.siteId] || {}) });
        }
        setLocalDataList(viewDataList);
        
    }, [dataList, projectList, sitesList]);

    useEffect(() => {
        const role = user?.user?.role;
        setShowCreateBtn(HPermission.hasPermission(role, HPermission.POLES_CREATE));
        setShowEditBtn(HPermission.hasPermission(role, HPermission.POLES_UPDATE));
        setShowDeleteBtn(HPermission.hasPermission(role, HPermission.POLES_DELETE));
    }, [user]);

    useEffect(() => {
        const columns = [...BaseTableColumns];
        if (showEditBtn || showDeleteBtn) {
            columns.push({
                title: ' ',
                render: item => (
                    <React.Fragment>
                        <Space wrap style={{ backgroundColor: themecolor.token.colorTextDisabled, padding: '11px' }} onClick={() => setSelectedDataRow(item)}>
                            {showEditBtn && (
                                <Button type="primary" ghost onClick={onEditBtnClick}>
                                    修改
                                </Button>
                            )}
                            {showDeleteBtn && (
                                <Button type="primary" danger ghost onClick={onDeleteBtnClick}>
                                    删除
                                </Button>
                            )}
                        </Space>
                    </React.Fragment>
                )
            });
        }
        setTableColumns(columns);
    }, [showEditBtn, showDeleteBtn]);

    function onDeleteBtnClick() {
        setDeleteModalIsOpen(true);
    }

    const onCreateBtnClick = () => {
        setSelectedDataRow(null);
        setIsCreateModel(true);
        setEditModalIsOpen(true);
    };

    const onEditBtnClick = () => {
        setEditModalIsOpen(true);
        setIsCreateModel(false);
    };

    const onDeleteCanceled = () => {
        setDeleteModalIsOpen(false);
        setSelectedDataRow(null);
    };

    const onDeleteConfirmed = () => {
        if (selectedDataRow) {
            dispatch(PoleThunk.remove(selectedDataRow.id));
        }

        setDeleteModalIsOpen(false);
        setSelectedDataRow(null);
    };

    async function onSubmitBtnClick(values) {
        if (isCreateModel) {
            dispatch(PoleThunk.create(values));
        } else {
            values.id = selectedDataRow.id;
            dispatch(PoleThunk.update(values));
        }

        setEditModalIsOpen(false);
    }

    // <--------------------- other configs --------------------->
    const pagination = {
        pageSizeOptions: ['5', '10', '20', '50'], // 页条数下拉选择器的选项
        defaultPageSize: 10, // 默认的页大小
        showSizeChanger: true, // 显示页大小改变器
    };

    return (
        <React.Fragment>
            {/* Delete */}
            <DeleteModal show={deleteModalIsOpen} handleClose={onDeleteCanceled} deleteModalFunction={onDeleteConfirmed} />

            {/* Api */}
            <Card style={{ marginTop: customStyles.margin, marginBottom: customStyles.margin }} className="ApiTable">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: customStyles.margin }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '500' }}>{TEXT_CONSTANTS.TITLE}</h4>
                    <div style={{ display: 'flex', flex: 'wrap' }}>
                        {showCreateBtn && (
                            <Button type="primary" style={{ backgroundColor: customStyles.colorSuccess, boxShadow: 'none' }} onClick={onCreateBtnClick}>
                                <PlusOutlined />
                                {TEXT_CONSTANTS.CREATE}
                            </Button>
                        )}
                    </div>
                </div>
                {isLoading ? (
                    <Spinners setLoading={setLoading} />
                ) : (
                    <React.Fragment>
                        {localDataList && localDataList.length > 0 ? (
                            <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                <Table className='table-list' rowKey="id" columns={tableColumns} dataSource={localDataList} pagination={pagination} scroll={{y:'calc(100vh - 408px)'}}/>
                            </div>
                        ) : (
                            <Empty image="/nodata.png" description={<span></span>} style={{ padding: '10%' }}></Empty>
                        )}
                    </React.Fragment>
                )}
            </Card>

            <PoleModal open={editModalIsOpen} isEdit={!isCreateModel} pole={selectedDataRow} onOk={onSubmitBtnClick} onOpenChange={setEditModalIsOpen} />
        </React.Fragment>
    );
}

export default DataList;
